import React from 'react';
import {Button, Message, getMessage} from 'epic-diesel-common';
import {buttonTypes, priority} from 'epic-diesel-common/src/constants';
import {ApplicationFormProps, Form} from '../../components/sections/application/ApplicationForm';

import {
    SocialChannelSection,
    EulaFormSection,
    GuardianSection,
    LegalAcknowledgementSection,
    RegionAndLanguageSection,
    MainSocialChannelSection,
    CodeSelfServiceSection,
    DisclaimerSection
} from '../../components';
import {canChangeCreatorCode} from '../../utils/ValidationUtils';

type Refs = Record<string, any>;

interface SignUpProps
    extends Omit<ApplicationFormProps, 'isEulasLoading' | 'talonState' | 'children'> {
    forwardedRefs: Refs;
    form: Form;
    styles: any;
    getMessage: (messages, code) => string;
    enableSubmit: boolean;
    updateForm: (params) => void;
    trigger: (k, v) => void;
    updateFormErrors: (params) => void;
    disconnectSocialChannel: (channel, severConnection?) => void;
    updateEulaStatus: (name: string, checked: boolean) => void;
    renderValidatedFormError: () => JSX.Element;
    showError: boolean;
}

const CODES = {
    disclaimer: {
        one: 'epic.affiliate.signup.disclaimer.creator_code.one.v2',
        two: 'epic.affiliate.signup.disclaimer.creator_code.two.v2',
        three: 'epic.affiliate.signup.disclaimer.creator_code.three.v2'
    },
    socialSection: {
        header: {
            default: 'epic.affiliate.social.channels.header',
            guardian: 'epic.affiliate.signup.guardian.social.channels.header'
        },
        subHeader: {
            default: 'epic.affiliate.social.text',
            guardian: 'epic.affiliate.signup.guardian.social.text'
        },
        desc: 'epic.affiliate.social.channels.textBottom'
    },
    privacyPolicy: 'epic.affiliate.common.privacyPolicy',
    contactSupport: 'epic.affiliate.common.contactSupport',
    cancel: 'epic.affiliate.common.cancel',
    submit: 'epic.affiliate.common.submit',
    displayName: 'epic.affiliate.common.displayName'
};

export const SignUp: React.FC<SignUpProps> = props => {
    const {form, styles, userState} = props;
    const {accountInfo} = userState || {};
    const {enableCodeSelfService, quickLinks = {}} = props.configState || {};
    const {channels = [], creatorCode} = form;
    const {affiliateAccount, codeAvailability} = props.influencerState || {};

    let enableSubmit = props.enableSubmit;

    if (enableCodeSelfService && !canChangeCreatorCode(creatorCode, codeAvailability)) {
        enableSubmit = false;
    }

    const sections = [
        <LegalAcknowledgementSection
            key="LegalSection"
            age18Acknowledgment={form.age18Acknowledgment}
            guardianAcknowledgment={form.guardianAcknowledgment}
            trigger={props.trigger}
        />,
        form.guardianAcknowledgment && (
            <GuardianSection
                key="GuardianSection"
                messages={props.messages}
                form={form.guardianInfo}
                trigger={props.trigger}
            />
        ),
        <div key="SocialSection">
            <h2>
                <Message
                    code={
                        form.guardianAcknowledgment
                            ? CODES.socialSection.header.guardian
                            : CODES.socialSection.header.default
                    }
                />
                {' *'}
            </h2>
            <p>
                <Message
                    code={
                        form.guardianAcknowledgment
                            ? CODES.socialSection.subHeader.guardian
                            : CODES.socialSection.subHeader.default
                    }
                />
            </p>

            <p>
                <Message code={CODES.socialSection.desc} />
            </p>
            <SocialChannelSection
                ref={props.forwardedRefs.addSocialRef}
                channels={form.channels}
                mainChannelPlatform={form.mainChannelPlatform}
                updateFormErrors={props.updateFormErrors}
                updateForm={props.updateForm}
                configState={props.configState}
                messages={props.messages}
                affiliate={affiliateAccount}
                disconnectSocialChannel={props.disconnectSocialChannel}
                getSocialChannels={props.getSocialChannels}
            />
        </div>,
        channels.length > 0 && (
            <MainSocialChannelSection
                key="MainSocialChannelSection"
                messages={props.messages}
                mainChannelPlatform={form.mainChannelPlatform}
                configState={props.configState}
                channels={form.channels}
                trigger={props.trigger}
                showError={props.showError}
            />
        ),
        enableCodeSelfService && (
            <CodeSelfServiceSection
                key="CodeSelfServiceSection"
                creatorCode={form.creatorCode}
                updateForm={code => props.trigger('creatorCode', code)}
            />
        ),
        <RegionAndLanguageSection
            key="RegionSection"
            messages={props.messages}
            form={form}
            trigger={props.trigger}
        />,
        <EulaFormSection
            key="EulaSection"
            messages={props.messages}
            guardianAccepted={form.guardianAccepted}
            guardianAcknowledgment={form.guardianAcknowledgment}
            acceptedAgreement={form.agreementAccepted}
            acceptedConduct={form.eulaAccepted}
            updateEulaStatus={props.updateEulaStatus}
        />,
        !enableCodeSelfService && (
            <DisclaimerSection
                key="DisclaimerSection"
                messages={props.messages}
                disclaimerAccepted={form.disclaimerAccepted}
                updateForm={props.trigger}
                configState={props.configState}
                userState={props.userState}>
                {accountInfo && (
                    <div className={styles.displayNameContainer}>
                        <Message code={CODES.displayName} />
                        <span className={styles.displayName}>{accountInfo.displayName}</span>
                    </div>
                )}

                <Message code={CODES.disclaimer.one} />

                <span
                    dangerouslySetInnerHTML={{
                        __html: getMessage(props.messages, CODES.disclaimer.two, [
                            quickLinks.changeDisplayName
                        ])
                    }}
                />

                <Message className={styles.italic} code={CODES.disclaimer.three} />
            </DisclaimerSection>
        ),
        <div key="SubmitSection" className={styles.submitSection}>
            {props.renderValidatedFormError()}

            <Button priority={priority.SECONDARY} to="overview" isExternalUrl={false}>
                <Message code={CODES.cancel} />
            </Button>

            <Button
                priority={priority.PRIMARY}
                type={buttonTypes.SUBMIT}
                disabled={!enableSubmit}
                theme="dark">
                <Message code={CODES.submit} />
            </Button>

            <ul className={styles.links}>
                <li>
                    <a href={quickLinks.privacyPolicy} target="_blank" rel="noopener noreferrer">
                        <Message code={CODES.privacyPolicy} />
                    </a>
                </li>
                <li>
                    <a href={quickLinks.contactSupport} target="_blank" rel="noopener noreferrer">
                        <Message code={CODES.contactSupport} />
                    </a>
                </li>
            </ul>
        </div>
    ];

    return (
        <>
            {sections.map((section, index) => {
                if (section) {
                    return (
                        <div key={`section-${index}`} className={styles.section}>
                            {section}
                        </div>
                    );
                }
            })}
        </>
    );
};

export default React.forwardRef((props: SignUpProps, ref: Refs) => {
    return <SignUp {...props} forwardedRefs={ref} />;
});
